import { useState } from 'react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { Search, Plus, Pencil, Eye, Trash2, Printer, Mail, Send, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { PoStatusStepper } from '@/components/shared/po-status-stepper';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  usePurchaseOrders,
  useResendPurchaseOrderEmail,
  useSendPurchaseOrder,
  useCancelPurchaseOrder,
} from '@/hooks/use-purchase-orders';
import { usePermissions } from '@/hooks/use-permissions';
import { useLocationStore } from '@/stores/location-store';
import { toast } from '@/stores/toast-store';
import { api } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

type Target = { id: string; orderNumber: number };

export function PurchaseOrderListPage() {
  // Each action has its own permission; deleting covers drafts only.
  const permissions = usePermissions();
  const canCreate = permissions.has('purchase_orders.create');
  const canEdit = permissions.has('purchase_orders.edit');
  const canSend = permissions.has('purchase_orders.send');
  const canCancel = permissions.has('purchase_orders.cancel');
  const canDelete = permissions.has('purchase_orders.delete');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Target | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resendTarget, setResendTarget] = useState<Target | null>(null);
  const [sendTarget, setSendTarget] = useState<Target | null>(null);
  const [cancelTarget, setCancelTarget] = useState<(Target & { status: string }) | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const queryClient = useQueryClient();
  const { selectedLocationId } = useLocationStore();
  const resendEmail = useResendPurchaseOrderEmail();
  const sendPO = useSendPurchaseOrder();
  const cancelPO = useCancelPurchaseOrder();

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // List all POs placed by the current location, regardless of sourceType — a PO
  // is placed BY this location whether the vendor is external or a warehouse.
  // (The "Warehouse Outgoing" tab shows orders this warehouse is FULFILLING —
  // different perspective, keyed by payees.linkedWarehouseLocationId.)
  const { data, isLoading } = usePurchaseOrders({
    page,
    perPage,
    search: search || undefined,
    status: statusFilter || undefined,
    locationId: selectedLocationId || undefined,
  });

  const orders = data?.data ?? [];
  const pagination = data?.pagination;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/purchase-orders/${deleteTarget.id}`);
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success(`Purchase Order #${deleteTarget.orderNumber} deleted`);
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(
        err?.error?.message || err?.message || `Failed to delete Purchase Order #${deleteTarget.orderNumber}`,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmResend = async () => {
    if (!resendTarget) return;
    try {
      const res = await resendEmail.mutateAsync(resendTarget.id);
      toast.success(`Purchase Order #${resendTarget.orderNumber} has been emailed to ${res.data.to}.`);
      setResendTarget(null);
    } catch (err: any) {
      toast.error(
        err?.error?.message ||
          err?.response?.data?.error?.message ||
          `We couldn’t resend Purchase Order #${resendTarget.orderNumber}. Please try again.`,
      );
    }
  };

  const handleConfirmSend = async () => {
    if (!sendTarget) return;
    try {
      const res: any = await sendPO.mutateAsync(sendTarget.id);
      const to = res?.sent?.to;
      toast.success(
        to
          ? `Purchase Order #${sendTarget.orderNumber} has been sent to ${to}.`
          : `Purchase Order #${sendTarget.orderNumber} has been sent to the vendor.`,
      );
      setSendTarget(null);
    } catch (err: any) {
      toast.error(
        err?.error?.message || `We couldn’t send Purchase Order #${sendTarget.orderNumber}. Please try again.`,
      );
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelPO.mutateAsync({ id: cancelTarget.id, reason: cancelReason.trim() || undefined });
      const wasSent = cancelTarget.status === 'sent';
      toast.success(`PO #${cancelTarget.orderNumber} cancelled${wasSent ? ' — vendor notified' : ''}`);
      setCancelTarget(null);
      setCancelReason('');
    } catch (err: any) {
      toast.error(err?.error?.message || `Failed to cancel PO #${cancelTarget.orderNumber}`);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'orderNumber',
      header: 'PO #',
      size: 70,
      cell: ({ row }) => <span className="font-medium text-primary">#{row.original.orderNumber}</span>,
    },
    {
      accessorKey: 'payeeName',
      header: 'Vendor',
      cell: ({ row }) => <span>{row.original.payeeName}</span>,
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
    },
    {
      accessorKey: 'createdByName',
      header: 'User',
      cell: ({ row }) => row.original.createdByName || 'System',
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      cell: ({ row }: any) =>
        row.original.purpose ? (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-blue-700">
            {row.original.purpose}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    // Total Cost column intentionally omitted — cost / total are hidden across
    // the entire PO flow (form, picker, and list) per buyer request. The
    // role-based useCanSeeCost gate is bypassed here so even users who could
    // legitimately see cost on other surfaces won't see it on the PO list.
    {
      accessorKey: 'status',
      header: 'Progress',
      cell: ({ row }) => <PoStatusStepper status={row.original.status} sourceType={row.original.sourceType} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 220,
      cell: ({ row }) => {
        const status = row.original.status as string;
        const isDraft = status === 'draft';
        const isSent = status === 'sent';
        // Once the warehouse has fulfilled the order, cancel is no longer
        // available — the order is locked from the buyer side. The buyer
        // should Mark Received and run the return flow if they refuse the
        // shipment. Cancelled / received / completed are also terminal.
        const isTerminal =
          status === 'cancelled' || status === 'received' || status === 'completed' || status === 'fulfilled';
        return (
          <div className="flex items-center gap-1">
            {/* Pencil for editable POs (draft / sent), eye for locked POs
                (fulfilled / received / cancelled). The destination route is
                the same — the form itself renders the right inputs based on
                status — but the icon and tooltip make the affordance honest. */}
            {(() => {
              const isReadOnly =
                !canEdit ||
                status === 'fulfilled' ||
                status === 'received' ||
                status === 'completed' ||
                status === 'cancelled';
              return (
                <a
                  href={`#/pos-management/purchase-orders/${row.original.id}/edit`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                  title={isReadOnly ? 'View' : 'Edit'}
                >
                  {isReadOnly ? (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Pencil className="h-4 w-4 text-primary" />
                  )}
                </a>
              );
            })()}
            <button
              onClick={() =>
                window.open(`#/pos-management/purchase-orders/${row.original.id}/print`, `po-print-${row.original.id}`)
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
              title="Print"
            >
              <Printer className="h-4 w-4 text-muted-foreground" />
            </button>
            {/* Draft → green Send button (primary action). Sent → re-send icon. */}
            {isDraft && canSend && (
              <button
                onClick={() => setSendTarget({ id: row.original.id, orderNumber: row.original.orderNumber })}
                disabled={sendPO.isPending}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                title="Send PO to vendor"
              >
                <Send className="h-3.5 w-3.5" />
                Send
              </button>
            )}
            {isSent && canSend && (
              <button
                onClick={() => setResendTarget({ id: row.original.id, orderNumber: row.original.orderNumber })}
                disabled={resendEmail.isPending}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted disabled:opacity-50"
                title="Re-send PO email to vendor"
              >
                <Mail className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            {/* Cancel is available for draft + sent only — fulfilled /
                received / cancelled are terminal. */}
            {!isTerminal && canCancel && (
              <button
                onClick={() => setCancelTarget({ id: row.original.id, orderNumber: row.original.orderNumber, status })}
                disabled={cancelPO.isPending}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-amber-50 disabled:opacity-50"
                title={isSent ? 'Cancel PO (vendor will be notified)' : 'Cancel PO'}
              >
                <XCircle className="h-4 w-4 text-amber-600" />
              </button>
            )}
            {/* Delete is only available for drafts — sent POs must be cancelled. */}
            {isDraft && canDelete && (
              <button
                onClick={() => setDeleteTarget({ id: row.original.id, orderNumber: row.original.orderNumber })}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                title="Delete (draft only)"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({ data: orders, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="space-y-4">
      <PageHeader title="Purchase Orders">
        {canCreate && (
          <a
            href="#/pos-management/purchase-orders/create"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create
          </a>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search purchase orders..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="received">Received</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        <>
          <DataTable table={table} columns={columns} />
          {pagination && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Purchase Order"
        message={`Permanently delete Purchase Order #${deleteTarget?.orderNumber}? This removes the PO and its line items and cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!resendTarget}
        title="Resend Purchase Order Email"
        message={`Resend Purchase Order #${resendTarget?.orderNumber} to the vendor’s contact on file? They will receive a fresh copy of the PO by email.`}
        confirmLabel="Send Email"
        cancelLabel="Cancel"
        variant="info"
        loading={resendEmail.isPending}
        onConfirm={handleConfirmResend}
        onCancel={() => setResendTarget(null)}
      />

      <ConfirmDialog
        open={!!sendTarget}
        title="Send Purchase Order to Vendor"
        message={`Send Purchase Order #${sendTarget?.orderNumber} to the vendor? They will receive an email containing the PO details and a link to confirm the order.`}
        confirmLabel="Send Email"
        cancelLabel="Cancel"
        variant="info"
        loading={sendPO.isPending}
        onConfirm={handleConfirmSend}
        onCancel={() => setSendTarget(null)}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel Purchase Order"
        message={
          cancelTarget?.status === 'sent'
            ? `Cancel Purchase Order #${cancelTarget?.orderNumber}? The vendor will receive a cancellation notice by email.`
            : `Cancel Purchase Order #${cancelTarget?.orderNumber}?`
        }
        confirmLabel="Cancel PO"
        cancelLabel="Keep"
        variant="warning"
        loading={cancelPO.isPending}
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          setCancelTarget(null);
          setCancelReason('');
        }}
      >
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          Reason (optional{cancelTarget?.status === 'sent' ? ' — sent to vendor' : ''})
        </label>
        <textarea
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          rows={2}
          placeholder="e.g. Wrong vendor selected"
          className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </ConfirmDialog>
    </div>
  );
}
